import React, { Suspense, lazy, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// Lazy loading des composants lourds (commenté car AudioPlayer n'existe pas encore)
// const LazyAudioPlayer = lazy(() => import('./AudioPlayer'));
const LazyInternalLinksEngine = lazy(() => import('./InternalLinksEngine'));

// Composant pour optimiser Core Web Vitals
const PerformanceOptimizer = ({ children }) => {
  useEffect(() => {
    // Préchargement des ressources critiques
    const preloadCriticalResources = () => {
      // Précharger les polices critiques
      const fontLink = document.createElement('link');
      fontLink.rel = 'preload';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
      fontLink.as = 'style';
      fontLink.onload = function() { this.onload = null; this.rel = 'stylesheet'; };
      document.head.appendChild(fontLink);

      // Précharger les sons populaires
      const popularSounds = [
        '/sounds/nature/vent-fort.mp3',
        '/sounds/effets/explosion.mp3',
        '/sounds/ambiance/cafe.mp3'
      ];
      
      popularSounds.forEach(soundUrl => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = soundUrl;
        document.head.appendChild(link);
      });
    };

    // Optimisation du Cumulative Layout Shift (CLS)
    const optimizeCLS = () => {
      // Réserver l'espace pour les images
      const images = document.querySelectorAll('img:not([width]):not([height])');
      images.forEach(img => {
        if (!img.style.aspectRatio) {
          img.style.aspectRatio = '16/9'; // Ratio par défaut
        }
      });
    };

    // Optimisation du First Input Delay (FID)
    const optimizeFID = () => {
      // Différer les scripts non critiques
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          preloadCriticalResources();
        });
      } else {
        setTimeout(preloadCriticalResources, 1);
      }
    };

    // Optimisation du Largest Contentful Paint (LCP)
    const optimizeLCP = () => {
      // Observer LCP pour identifier les éléments critiques
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          // Log pour debugging (à retirer en production)
          console.log('LCP candidate:', lastEntry.element, 'Time:', lastEntry.startTime);
        });
        
        try {
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {
          // Fallback pour navigateurs non supportés
        }
      }
    };

    // Exécuter les optimisations
    optimizeCLS();
    optimizeFID();
    optimizeLCP();

    // Nettoyage
    return () => {
      // Nettoyer les observers si nécessaire
    };
  }, []);

  return (
    <>
      <Helmet>
        {/* DNS Prefetch pour les domaines externes */}
        <link rel="dns-prefetch" href="//fonts.googleapis.com" />
        <link rel="dns-prefetch" href="//lasonotheque.org" />
        <link rel="dns-prefetch" href="//vercel.app" />
        
        {/* Preconnect pour les ressources critiques */}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        
        {/* Resource hints pour améliorer les performances */}
        <link rel="prefetch" href="/sounds.json" />
        <link rel="prefetch" href="/categories.json" />
        
        {/* Optimisations pour Core Web Vitals */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        
        {/* Hints pour le navigateur */}
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      </Helmet>
      
      {/* Suspense pour le lazy loading */}
      <Suspense fallback={
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          background: 'linear-gradient(90deg, #f0f0f0 25%, transparent 37%, #f0f0f0 63%)',
          backgroundSize: '400% 100%',
          animation: 'shimmer 1.5s ease-in-out infinite'
        }}>
          <div>Chargement...</div>
        </div>
      }>
        {children}
      </Suspense>
    </>
  );
};

// Hook pour mesurer les Core Web Vitals
export const useWebVitals = () => {
  useEffect(() => {
    // Importer et utiliser web-vitals uniquement côté client
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      const sendToAnalytics = (metric) => {
        // En production, envoyer à Google Analytics ou autre service
        console.log('Web Vital:', metric.name, metric.value, metric.rating);
        
        // Exemple d'envoi à Google Analytics 4
        if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
          window.gtag('event', metric.name, {
            event_category: 'Web Vitals',
            event_label: metric.id,
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            non_interaction: true,
          });
        }
      };

      // Mesurer tous les Core Web Vitals
      getCLS(sendToAnalytics);
      getFID(sendToAnalytics);
      getFCP(sendToAnalytics);
      getLCP(sendToAnalytics);
      getTTFB(sendToAnalytics);
    }).catch(err => {
      console.warn('Web Vitals non disponibles:', err);
    });
  }, []);
};

// Composant pour optimiser les images
export const OptimizedImage = ({ src, alt, width, height, className, ...props }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const imgRef = React.useRef();

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={imgRef}
      className={className}
      style={{ 
        aspectRatio: width && height ? `${width}/${height}` : '16/9',
        background: isLoaded ? 'transparent' : '#f0f0f0',
        transition: 'background 0.3s ease'
      }}
      {...props}
    >
      {isInView && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
    </div>
  );
};

export default PerformanceOptimizer;
